import React, {
  useState, useContext, createContext, useEffect,
} from 'react';
import PropTypes from 'prop-types';

import { getClientInformation } from '../api/services/ClientInfo';
import getVerbalRoiContacts from '../api/services/verbalROI';
import getRoiContacts from '../api/services/roi';

const clientContext = createContext({
  clientId: undefined,
  client: undefined,
  error: undefined,
  displayError: undefined,
  clearError: undefined,
  setClientId: undefined,
  getClientInfo: undefined,
  clearClient: undefined,
  verbalRoiContacts: undefined,
});

export const useClientContext = () => useContext(clientContext);

const useProvideClientContext = () => {
  const [clientId, setClientId] = useState('');
  const [client, setClient] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    phone: '',
    email: '',
    label: '',
    mcdId: '',
    contactPreference: '',
    valid: false,
  });
  const [error, setError] = useState('');
  const [verbalRoiContacts, setVerbalRoiContacts] = useState([]);
  const [roiContacts, setRoiContacts] = useState([]);

  useEffect(() => {
    if (client.valid) {
      getVerbalRoiContacts(clientId)
        .then((response) => {
          setVerbalRoiContacts(response.data.data.contacts);
        });
    }
  }, [client, setVerbalRoiContacts, getVerbalRoiContacts]);

  useEffect(() => {
    if (client.valid) {
      getRoiContacts(clientId).then((response) => {
        setRoiContacts(response.data.data.contacts);
      });
    }
  }, [client, setRoiContacts, getRoiContacts]);

  const displayError = (message, debug) => {
    setError({ message, debug });
  };

  const clearError = () => {
    setError(undefined);
  };

  const getClientInfo = () => {
    getClientInformation(clientId)
      .then((response) => {
        const { data } = response.data;
        setClient((prev) => ({
          ...prev,
          firstName: data.firstName,
          lastName: data.lastName,
          dateOfBirth: data.dateOfBirth,
          phone: data.phone,
          email: data.email,
          label: `${data.lastName}, ${data.firstName} ${data.dateOfBirth}`,
          valid: true,
        }));
      })
      .catch((err) => {
        displayError(err.message);
      });
  };

  const clearClient = () => {
    setClient({
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      phone: '',
      email: '',
      label: '',
      mcdId: '',
      contactPreference: '',
      valid: false,
    });
    setClientId('');
    setError('');
    setVerbalRoiContacts([]);
  };

  return {
    clientId,
    client,
    error,
    displayError,
    clearError,
    setClientId,
    setClient,
    getClientInfo,
    clearClient,
    verbalRoiContacts,
    roiContacts,
  };
};

const ProvideClientContext = ({ children }) => {
  const client = useProvideClientContext();
  return (
    <clientContext.Provider value={client}>
      {children}
    </clientContext.Provider>
  );
};

export default ProvideClientContext;

ProvideClientContext.propTypes = {
  children: PropTypes.element.isRequired,
};
